import { useState } from "react";
import { useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useLogSignature,
  useResolveSignature,
  useMuteSignature,
  type LogRange,
  type LogSignature,
} from "@/hooks/api";
import { cn } from "@/lib/cn";

const nf = new Intl.NumberFormat("nb-NO");
const timeFmt = new Intl.DateTimeFormat("nb-NO", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
const dateFmt = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const BUCKET_LABEL = {
  action: "KREVER HANDLING",
  auth: "AVVIST · AUTH",
  noise: "SKANNING · KARANTENE",
} as const;

const BADGE = {
  action: "bg-admin-danger-950 text-admin-danger-300",
  auth: "bg-admin-warn-950 text-admin-warn-400",
  noise: "bg-admin-slate-badge text-admin-text-subtle",
} as const;

type Tab = "events" | "payload" | "routes";

const TABS: { key: Tab; label: string }[] = [
  { key: "events", label: "Hendelser" },
  { key: "payload", label: "Payload" },
  { key: "routes", label: "Ruter i signaturen" },
];

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] tracking-[0.06em] text-admin-text-subtle mb-1">{label}</div>
      <div className="font-mono text-sm text-admin-text-primary">{value}</div>
    </div>
  );
}

export function SignatureDetail({
  signature,
  range,
  row,
}: {
  signature: string | null;
  range: LogRange;
  row: LogSignature | undefined;
}) {
  const [tab, setTab] = useState<Tab>("events");
  const [params, setParams] = useSearchParams();
  const detail = useLogSignature(signature, range);
  const resolve = useResolveSignature();
  const mute = useMuteSignature();

  // The bridge from triage to tracing: Rålogg is the only view that shows the
  // info/debug lines written during the request, so hand it the request id.
  const openInRawLogs = (requestId: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", "raa");
    next.set("requestId", requestId);
    setParams(next);
  };

  if (!signature) {
    return (
      <div className="bg-admin-card border border-admin-card-border rounded-[10px] p-6 text-[12.5px] text-admin-text-subtle">
        Velg en signatur for å se detaljer.
      </div>
    );
  }

  if (detail.isLoading || !detail.data) {
    return (
      <div className="bg-admin-card border border-admin-card-border rounded-[10px] p-8 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  const d = detail.data;
  const maxBar = Math.max(1, ...d.histogram);
  const muted = !!row?.mutedUntil && new Date(row.mutedUntil) > new Date();

  return (
    <div className="bg-admin-card border border-admin-card-border rounded-[10px] overflow-hidden sticky top-5">
      <div className="px-4 py-4 border-b border-admin-card-border flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <span className={cn("font-mono text-[11px] font-bold px-1.5 py-[3px] rounded", BADGE[d.bucket])}>
            {d.status ?? "JOBB"}
          </span>
          <span className="text-[10.5px] tracking-[0.05em] text-admin-text-subtle">
            {BUCKET_LABEL[d.bucket]}
          </span>
        </div>
        <div className="font-mono text-[13.5px] leading-snug break-all text-admin-text-primary">
          {d.signature}
        </div>
        {d.diagnosis && (
          <div className="text-[12.5px] leading-relaxed text-admin-text-secondary">
            {d.diagnosis}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 border-b border-admin-card-border divide-x divide-admin-card-border">
        <Stat label="HENDELSER" value={nf.format(d.count)} />
        <Stat
          label="FØRSTE"
          value={d.firstSeen ? dateFmt.format(new Date(d.firstSeen)) : "—"}
        />
        <Stat label="BRUKERE" value={nf.format(d.users)} />
      </div>

      <div className="px-4 py-3.5 border-b border-admin-card-border flex flex-col gap-2">
        <div className="text-[10px] tracking-[0.06em] text-admin-text-subtle">
          SISTE {range === "1h" ? "TIMEN" : range === "24h" ? "24 TIMER" : range.toUpperCase()}
        </div>
        <div className="flex items-end gap-0.5 h-11">
          {d.histogram.map((v, i) => (
            <div
              key={i}
              className="flex-1 rounded-[1px]"
              style={{
                height: `${Math.max(2, (v / maxBar) * 44)}px`,
                background: v > 0 && i >= d.histogram.length - 4 ? "#f87171" : "#3f4653",
              }}
            />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[10px] text-admin-text-subtle/60">
          <span>start</span>
          <span>midt</span>
          <span>nå</span>
        </div>
      </div>

      <div className="flex border-b border-admin-card-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-4 py-2.5 text-xs cursor-pointer border-b-2 transition-colors duration-[120ms]",
              tab === t.key
                ? "text-admin-text-primary border-admin-primary"
                : "text-admin-text-secondary border-transparent"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-3.5 border-b border-admin-card-border">
        {tab === "events" && (
          <div className="flex flex-col gap-1.5">
            {d.events.length === 0 && (
              <div className="text-[11.5px] text-admin-text-subtle">Ingen hendelser i perioden.</div>
            )}
            {d.events.map((e, i) => (
              <div
                key={i}
                className="flex gap-2.5 font-mono text-[11.5px] py-1.5 border-b border-admin-slate-divider last:border-0"
              >
                <span className="text-admin-text-subtle shrink-0 w-[50px]">
                  {timeFmt.format(new Date(e.createdAt))}
                </span>
                <span className="text-admin-text-secondary shrink-0 w-[76px] truncate">
                  {e.jobId ? `jobb ${e.jobId}` : "—"}
                </span>
                <span
                  className="text-admin-text-secondary shrink-0 w-[64px] truncate"
                  title={e.authUserId ?? undefined}
                >
                  {e.authUserId ? e.authUserId.slice(0, 8) : "—"}
                </span>
                {e.requestId ? (
                  <button
                    type="button"
                    title="Følg forespørselen i Rålogg"
                    className="text-admin-primary shrink-0 w-[68px] text-left hover:underline cursor-pointer"
                    onClick={() => openInRawLogs(e.requestId!)}
                  >
                    {e.requestId}
                  </button>
                ) : (
                  <span className="text-admin-text-subtle shrink-0 w-[68px]">—</span>
                )}
                {/* Must not clip: this is where the job id or row number lives. */}
                <span className="text-admin-text-subtle leading-snug break-words">{e.message}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "payload" && (
          <pre className="bg-admin-bg border border-admin-card-border rounded-[7px] px-3 py-2.5 font-mono text-[11.5px] leading-relaxed text-admin-text-secondary whitespace-pre-wrap break-words overflow-x-auto">
            {d.payload ?? "Ingen payload registrert."}
          </pre>
        )}

        {tab === "routes" && (
          <div className="flex flex-col gap-1.5">
            {d.routes.length === 0 && (
              <div className="text-[11.5px] text-admin-text-subtle">
                Ingen konkrete ruter registrert for denne signaturen.
              </div>
            )}
            {d.routes.map((r) => (
              <div
                key={r.path}
                className="flex gap-2.5 items-baseline font-mono text-[11.5px] py-1.5 border-b border-admin-slate-divider last:border-0"
              >
                <span className="flex-1 text-admin-text-secondary break-all">{r.path}</span>
                <span className="text-admin-text-subtle">{nf.format(r.count)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3.5 flex gap-2 flex-wrap">
        <button
          onClick={() =>
            resolve.mutate(
              { signature: d.signature },
              {
                onSuccess: () => toast.success("Markert som løst"),
                onError: (e: any) => toast.error(e?.message ?? "Feil"),
              }
            )
          }
          disabled={resolve.isPending}
          className="px-3.5 py-2 rounded-[7px] bg-admin-primary hover:bg-admin-primary-hover text-admin-primary-fg text-[13px] font-semibold cursor-pointer disabled:opacity-60"
        >
          Marker løst
        </button>
        <button
          onClick={() =>
            mute.mutate(
              { signature: d.signature, days: muted ? 0 : 7 },
              {
                onSuccess: () => toast.success(muted ? "Dempingen er fjernet" : "Dempet i 7 dager"),
                onError: (e: any) => toast.error(e?.message ?? "Feil"),
              }
            )
          }
          disabled={mute.isPending}
          className="px-3.5 py-2 rounded-[7px] border border-admin-card-border text-admin-text-secondary text-[13px] cursor-pointer hover:bg-admin-card-hover disabled:opacity-60"
        >
          {muted ? "Fjern demping" : "Demp 7 d"}
        </button>
        {row?.jobId && (
          <a
            href="/system?tab=jobs"
            className="px-3.5 py-2 rounded-[7px] border border-admin-card-border text-admin-text-secondary text-[13px] cursor-pointer hover:bg-admin-card-hover"
          >
            Åpne jobber
          </a>
        )}
      </div>
    </div>
  );
}
